import { useState } from 'react'
import PrimaryButton from '../../global/components/buttons/primary-button/PrimaryButton'
import LocationDropDown from '../../global/components/location-drop-down/LocationDropDown'
import { TextField, Alert } from '@mui/material'
import styles from './styles.module.css'
import { fetchData } from '../../global/utils/fetch'
import { moveSchedulingDataToNewDay } from '../api'
import { formatDateYyyyMmDd } from '../../global/utils/date/date-utils-yyyy-mm-dd'

interface MoveSchedulingDataResponse {
  success: boolean
  message: string
  locationName: string
  sourceDate: string
  targetDate: string
  routesMoved: number
  ordersMoved: number
  ordersReactivated: number
  daySettingsCreated: number
  dayPlanMoved: boolean
  deliveryCandidatesMoved: number
  details: {
    routes: string[]
    orders: { objectId: string; workOrderId: number; previousStatus: string }[]
    technicians: string[]
  }
}

export default function MoveSchedulingDataToNewDay() {
  const isProduction = process.env.REACT_APP_ENV === 'prod'

  const [form, setForm] = useState({
    locationObjectId: '',
    sourceDate: formatDateYyyyMmDd(new Date()),
    targetDate: formatDateYyyyMmDd(new Date()),
  })
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<MoveSchedulingDataResponse | null>(null)

  async function onMoveClick() {
    const { locationObjectId, sourceDate, targetDate } = form
    if (!locationObjectId) return alert('Please select a location')
    if (!sourceDate) return alert('Please select a source date')
    if (!targetDate) return alert('Please select a target date')
    if (sourceDate === targetDate)
      return alert('Source and target dates must be different')

    const confirmMessage = `Are you sure you want to move all scheduling data from ${sourceDate} to ${targetDate}? This will:\n\n` +
      `- Move all routes and orders to the new date\n` +
      `- Move all delivery candidates to the new date\n` +
      `- Reactivate any completed/cancelled_onsite orders to "scheduled"\n` +
      `- Copy day settings for affected technicians\n` +
      `- Move the day plan (and delete the source day plan)\n\n` +
      `This action cannot be easily undone.`

    if (!window.confirm(confirmMessage)) return

    setLoading(true)
    setResult(null)
    try {
      const response = (await fetchData(
        moveSchedulingDataToNewDay(locationObjectId, sourceDate, targetDate),
      )) as MoveSchedulingDataResponse
      setResult(response)
    } catch (error: any) {
      console.error(error)
      alert(`Move failed: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  if (isProduction) {
    return (
      <div className={styles.toolContainer}>
        <h2>Move Scheduling Data to New Day</h2>
        <Alert severity='error'>
          This tool is disabled in production environment.
        </Alert>
      </div>
    )
  }

  return (
    <div className={styles.toolContainer}>
      <h2>Move Scheduling Data to New Day</h2>
      <p className={styles.toolDescription}>
        Move all scheduling data (routes, orders, timeslots, day settings,
        delivery candidates) from one day to another. Completed/cancelled orders
        will be reactivated.
      </p>

      <div className={styles.formContainer}>
        <LocationDropDown
          onLocationChange={(locationObjectId: string) =>
            setForm({ ...form, locationObjectId })
          }
          defaultValue={form.locationObjectId}
        />

        <TextField
          type='date'
          label='Source Date (move FROM)'
          name='sourceDate'
          value={form.sourceDate}
          onChange={(e) => setForm({ ...form, sourceDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        <TextField
          type='date'
          label='Target Date (move TO)'
          name='targetDate'
          value={form.targetDate}
          onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        <div className={styles.buttonContainer}>
          <PrimaryButton
            buttonName={loading ? 'Moving Data...' : 'Move Scheduling Data'}
            onClick={onMoveClick}
            disabled={loading}
          />
        </div>
      </div>

      {result && (
        <div style={{ marginTop: '1rem' }}>
          <Alert severity={result.success ? 'success' : 'error'}>
            {result.message}
          </Alert>
          {result.success && (
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '0.875rem',
              }}
            >
              <p>
                <strong>Routes moved:</strong> {result.routesMoved}
              </p>
              <p>
                <strong>Orders moved:</strong> {result.ordersMoved}
              </p>
              <p>
                <strong>Orders reactivated:</strong> {result.ordersReactivated}
              </p>
              <p>
                <strong>Day settings created:</strong>{' '}
                {result.daySettingsCreated}
              </p>
              <p>
                <strong>Day plan moved:</strong>{' '}
                {result.dayPlanMoved ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Delivery candidates moved:</strong>{' '}
                {result.deliveryCandidatesMoved}
              </p>
              {result.details.orders.length > 0 && (
                <details style={{ marginTop: '0.5rem' }}>
                  <summary>Order Details ({result.details.orders.length})</summary>
                  <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                    {result.details.orders.map((order) => (
                      <li key={order.objectId}>
                        WO#{order.workOrderId} (was: {order.previousStatus})
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
